import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Plus,
  Palette,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
  X,
  Trash2,
  ArrowRightCircle,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  Pencil,
  ExternalLink,
  Sparkles,
  UploadCloud,
  Download,
} from "lucide-react";
import UiBuilderModal from "../components/ViewModal";
import FilterBuilderComponent from "../components/FilterBuilderComponent";
import ReferenceModal from "../components/ReferenceModal";
import ImportWizard from "../components/ImportWizard";
import ModelTrainingWizard from "../components/ModelTrainingWizard";

const ListView = ({
  referenceFilter = null,
  externalFilters = {},
  hideHeader = false,
}) => {
  const [fields, setFields] = useState([]);
  const [records, setRecords] = useState([]);
  const [tableId, setTableId] = useState(null);
  const [tableLabel, setTableLabel] = useState("");
  const navigate = useNavigate();
  const { tableName: paramTableName } = useParams();
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit, setLimit] = useState(parseInt(searchParams.get("limit")) || 20);
  const [offset, setOffset] = useState(parseInt(searchParams.get("offset")) || 0);
  const [filterJson, setFilterJson] = useState(searchParams.get("filter_json") || "");
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  // group by state
  const [groupBy1, setGroupBy1] = useState("");
  const [groupBy2, setGroupBy2] = useState("");
  const [metricFn, setMetricFn] = useState("count"); // count | sum | avg | min | max
  const [metricField, setMetricField] = useState("id"); // default = id
  const isGrouped = !!groupBy1;

  const resolvedTableName =
    referenceFilter?.table || externalFilters.__tableName || paramTableName;

  const token = localStorage.getItem("token");
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const [isUiBuilderOpen, setUiBuilderOpen] = useState(false);
  const [availableViews, setAvailableViews] = useState([]);
  const [selectedViewId, setSelectedViewId] = useState(null);
  const [activeViewColumns, setActiveViewColumns] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkActions, setBulkActions] = useState([]);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [modelWizardOpen, setModelWizardOpen] = useState(false);
  // --- select-all (visible page) helpers ---
const headerCbRef = useRef(null);

// IDs on the *current* page (visible records)
const pageIds = useMemo(() => records.map(r => r.id), [records]);

// How many of the current page are selected
const pageSelectedCount = useMemo(
  () => pageIds.filter(id => selectedRows.includes(id)).length,
  [pageIds, selectedRows]
);

// Keep the header checkbox in "indeterminate" state when some (not all) on page are selected
useEffect(() => {
  if (!headerCbRef.current) return;
  headerCbRef.current.indeterminate =
    pageSelectedCount > 0 && pageSelectedCount < pageIds.length;
}, [pageSelectedCount, pageIds.length]);

// Optional: when the page changes, drop selections that are no longer visible
useEffect(() => {
  setSelectedRows(prev => prev.filter(id => pageIds.includes(id)));
}, [pageIds]);

// Custom bulk UI Actions (view_type: "list") -- mirrors UiActionBar.jsx's
// own view_type: "form" fetch. Loaded once per table regardless of
// selection so the bar can show them the moment rows are checked.
useEffect(() => {
  if (!resolvedTableName || !token) {
    setBulkActions([]);
    return;
  }
  axios
    .post(
      `${apiBaseUrl}/ui-actions/visible`,
      { table_name: resolvedTableName, view_type: "list", record_id: null, selected_ids: [] },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    .then((res) => setBulkActions(res.data?.actions || []))
    .catch(() => setBulkActions([]));
}, [resolvedTableName, token, apiBaseUrl]);

const runBulkAction = async (action) => {
  if (!selectedRows.length) return;
  if (action.sip_requires_confirmation) {
    const ok = window.confirm(action.sip_confirm_text || `Run "${action.sip_label || action.sip_name}" on ${selectedRows.length} record(s)?`);
    if (!ok) return;
  }
  try {
    const res = await axios.post(
      `${apiBaseUrl}/ui-actions/${action.id}/execute`,
      { table_name: resolvedTableName, record_id: null, selected_ids: selectedRows, client_payload: {} },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    alert(res.data?.message || "✅ Action completed.");
    setSelectedRows([]);
    if (res.data?.refresh !== false) setSearchTrigger((p) => p + 1);
  } catch (err) {
    console.error("❌ Bulk action failed:", err);
    alert(err.response?.data?.detail || "❌ Bulk action failed.");
  }
};

  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  // inline edit state
  const [editing, setEditing] = useState(null); // {id, field}
  const [draft, setDraft] = useState("");
  const [savingCells, setSavingCells] = useState(new Set()); // "id:field"

  // reference picker state
  const [refPicker, setRefPicker] = useState({
    open: false,
    row: null,        // whole record
    field: null,      // field meta
  });

  const isEditableField = (field) => {
    if (!field) return false;
    if (field.name === "id") return false;
    if (field.is_read_only) return false;
    if (field.acl_writable === false) return false;
    return true;
  };

  const keyOf = (rowId, fieldName) => `${rowId}:${fieldName}`;

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("limit", limit);
    newParams.set("offset", offset);
    if (filterJson) newParams.set("filter_json", filterJson);
    else newParams.delete("filter_json");
    setSearchParams(newParams, { replace: true });
  }, [limit, offset, filterJson]); // eslint-disable-line

  useEffect(() => {
    if (!resolvedTableName || !token || !fields.length) return;
    setSearchTrigger((prev) => prev + 1);
  }, [fields]); // eslint-disable-line

  useEffect(() => {
    if (!resolvedTableName || !token || !fields.length) return;

    const params = new URLSearchParams();

    if (referenceFilter?.field && referenceFilter?.value) {
      params.append(`${referenceFilter.field}__eq`, referenceFilter.value);
    }

    const mergedFilters = { ...columnFilters, ...externalFilters };
    for (const key in mergedFilters) {
      if (!Object.prototype.hasOwnProperty.call(mergedFilters, key)) continue;
      const rawVal = mergedFilters[key];
      if (typeof rawVal !== "string") continue;
      const val = rawVal.trim();
      if (!val) continue;

      const field = fields.find((f) => f.name === key);
      if (!field) continue;
      const type = (field.field_type || "").toUpperCase();

      let operator = "startsWith";
      let valueToSend = val;
      if (rawVal.startsWith("*")) {
        operator = "contains";
        valueToSend = rawVal.slice(1);
      }
      const isText = type.includes("CHAR") || type.includes("TEXT") || type.includes("ENUM");
      if (!isText) {
        if (type.includes("INT") || type.includes("NUMERIC") || type.includes("DECIMAL")) {
          operator = "in";
        } else if (type.includes("BOOL")) {
          operator = "in";
          const t = val.toLowerCase();
          valueToSend = t === "true" || t === "1" || t === "yes" ? "true" : "false";
        } else if (type.includes("DATE") || type.includes("TIME")) {
          operator = rawVal.startsWith("*") ? "contains" : "startsWith";
        }
      }
      params.append(`${key}__${operator}`, valueToSend);
    }

    const fullParams = new URLSearchParams(params.toString());
if (filterJson) fullParams.set("filter_json", filterJson);
fullParams.set("limit", limit);
fullParams.set("offset", offset);
if (sortBy) {
  fullParams.set("sort_by", sortBy);
  fullParams.set("sort_order", sortOrder);
}
if (isGrouped) {
  const agg = {
    group_by: [groupBy1, ...(groupBy2 ? [groupBy2] : [])],
    metrics: [{ field: metricFn === "count" ? (metricField || "id") : (metricField || "id"), function: metricFn }],
    resolve_display: true
  };
  fullParams.set("aggregate", JSON.stringify(agg));
}

    setLoading(true);
    axios
      .get(`${apiBaseUrl}/search/${resolvedTableName}/?${fullParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setRecords(res.data.records || []);
        setTotalRecords(res.data.total ?? res.data.records?.length ?? 0);
        setInitialLoaded(true); 
      })
      .catch((err) => console.error("❌ Failed to fetch filtered records:", err))
      .finally(() => setLoading(false));
  }, [searchTrigger, referenceFilter, limit, offset, sortBy, sortOrder]); // eslint-disable-line

  useEffect(() => {
    if (!resolvedTableName) return;
    axios
      .get(`${apiBaseUrl}/tables/`, { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        const found = response.data.find((t) => t.name === resolvedTableName);
        if (found) {
          setTableId(found.id);
          setTableLabel(found.label);
        }
      });
  }, [resolvedTableName, token, apiBaseUrl]);

  useEffect(() => {
    if (!tableId) return;

    axios
  .get(`${apiBaseUrl}/search/table_fields/?table_id=${tableId}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  .then(async (res) => {
    const fieldList = res.data.records || [];

    // fetch choices for ENUM fields
    const enumFields = fieldList.filter((f) => isEnumField(f) && f.id);
    if (enumFields.length) {
      const pairs = await Promise.all(
        enumFields.map(async (f) => {
          try {
            const r = await axios.get(
              `${apiBaseUrl}/table_field_choices/?table_field_id=${f.id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const rows = Array.isArray(r.data)
              ? r.data
              : Array.isArray(r.data?.records)
              ? r.data.records
              : [];
            const options = mapChoiceRowsToOptions(rows);
            return { id: f.id, options };
          } catch (e) {
            console.error("Failed to load choices for", f.name, f.id, e);
            return { id: f.id, options: [] };
          }
        })
      );

      for (const { id, options } of pairs) {
        const idx = fieldList.findIndex((x) => x.id === id);
        if (idx !== -1) {
          fieldList[idx].allowed_options = options;
          fieldList[idx].allowed_values = options.map((o) => o.value);
        }
      }
    }

    setFields(fieldList);
  });


    axios
      .get(`${apiBaseUrl}/search/views/?sip_table=${tableId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(async (res) => {
        const listViews = (res.data.records || []).filter((v) => v.sip_view_type === "list");
        setAvailableViews(listViews);

        const def = listViews.find((v) => v.sip_is_default);
        if (def) {
          setSelectedViewId(def.id);
          
          const cols = await axios.get(
            `${apiBaseUrl}/search/view_columns/?sip_view_id=${def.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const map = {};
          (cols.data.records || []).forEach((c) => {
            map[c.sip_field_name] = { order: c.sip_column_order || 0 };
          });
          setActiveViewColumns(map);
        }
      });
  }, [tableId, resolvedTableName, token, apiBaseUrl]);

  useEffect(() => {
    if (!selectedViewId) return;
    axios
      .get(`${apiBaseUrl}/search/view_columns/?sip_view_id=${selectedViewId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const map = {};
        (res.data.records || []).forEach((c) => {
          map[c.sip_field_name] = { order: c.sip_column_order || 0 };
        });
        setActiveViewColumns(map);
        setSearchTrigger((p) => p + 1);
      });
  }, [selectedViewId, token, apiBaseUrl]);

 function formatValue(value, field, record) {
  if (value === null || value === undefined) return "—";
  const type = (field?.field_type || "").toUpperCase();

  // reference: keep as-is
  if (typeof value === "object" && value.display_value && value.id) {
    return (
      <HoverableLink href={`/dashboard/${field.reference_table || resolvedTableName}/form_view/${value.id}`}>
        <span style={styles.badge}>{value.display_value}</span>
      </HoverableLink>
    );
  }

  // ENUM: map value -> label
  if (type === "ENUM") {
    const opt = field.allowed_options?.find((o) => String(o.value) === String(value));
    const label = opt?.label ?? String(value);
    return <span style={styles.badge}>{label}</span>;
  }

  if (type.includes("DATE") || type.includes("TIME")) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
  }

  return (
    <HoverableLink href={`/dashboard/${resolvedTableName}/form_view/${record.id}`}>
      {String(value)}
    </HoverableLink>
  );
}


  const toInputValue = (field, value) => {
    if (value === null || value === undefined) return "";
    const t = (field.field_type || "").toUpperCase();
    if (t.includes("TIMESTAMP")) {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    }
    if (t.includes("DATE") && !t.includes("TIME")) {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().slice(0, 10);
    }
    return String(value);
  };

  const fromInputValue = (field, raw) => {
    const t = (field.field_type || "").toUpperCase();
    if (raw === "" || raw === null || raw === undefined) {
      if (t.includes("BOOLEAN")) return false;
      return null;
    }
    if (t.includes("INT") || t.includes("NUMERIC") || t.includes("DECIMAL")) {
      const n = Number(raw);
      return Number.isNaN(n) ? null : n;
    }
    if (t.includes("BOOLEAN")) {
      if (typeof raw === "boolean") return raw;
      const s = String(raw).toLowerCase();
      return s === "true" || s === "1" || s === "yes";
    }
    if (t.includes("TIMESTAMP")) {
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? null : d.toISOString();
    }
    if (t.includes("DATE") && !t.includes("TIME")) {
      const d = new Date(raw + "T00:00:00");
      return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
    }
    return raw;
  };
const isEnumField = (f) => String(f?.field_type || "").trim().toUpperCase() === "ENUM";

const mapChoiceRowsToOptions = (rows) => {
  const pick = (a, b) => (a !== undefined && a !== null ? a : b);

  return rows
    .map((r) => {
      const rawValue = pick(r.sip_value, r.value);
      const value = String(rawValue ?? "");

      const rawLabel = pick(r.sip_label, r.label);
      const label = String(
        rawLabel === "" || rawLabel == null ? value : rawLabel
      ).trim();

      const rawActive = pick(r.sip_is_active, r.is_active);
      const active =
        rawActive === true ||
        rawActive === "t" ||
        rawActive === "true" ||
        rawActive === "1" ||
        rawActive === 1;

      const rawOrder = pick(r.sip_order, r.order);
      const orderNum = Number(rawOrder);
      const order = Number.isFinite(orderNum) ? orderNum : 0;

      return { value, label, order, is_active: active };
    })
    .filter((o) => o.is_active)
    .sort((a, b) => a.order - b.order);
};

const startEdit = (record, field) => {
  if (!isEditableField(field)) return;
  if (field.reference_table) {
    setRefPicker({ open: true, row: record, field });
    return;
  }
  setEditing({ id: record.id, field: field.name });
  setDraft(toInputValue(field, record[field.name])); // for ENUM this becomes String(value)
};


  const cancelEdit = () => {
    setEditing(null);
    setDraft("");
  };

  const commitEdit = async () => {
    if (!editing) return;
    const { id, field: fieldName } = editing;
    const field = fields.find((f) => f.name === fieldName);
    if (!field) return;

    const rec = records.find((r) => r.id === id);
    if (!rec) return;

    const oldVal = rec[fieldName];
    const newVal = fromInputValue(field, draft);

    const toComparable = (v) => (v && typeof v === "object" && "id" in v ? v.id : v);
    if (toComparable(oldVal) === toComparable(newVal)) {
      cancelEdit();
      return;
    }

    const cellKey = keyOf(id, fieldName);
    setSavingCells((s) => new Set([...s, cellKey]));

    setRecords((list) => list.map((r) => (r.id === id ? { ...r, [fieldName]: newVal } : r)));

    try {
      await axios.put(
        `${apiBaseUrl}/${resolvedTableName}/${id}`,
        { [fieldName]: newVal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setRecords((list) => list.map((r) => (r.id === id ? { ...r, [fieldName]: oldVal } : r)));
      alert("Failed to save change.");
    } finally {
      setSavingCells((s) => {
        const n = new Set(s);
        n.delete(cellKey);
        return n;
      });
      cancelEdit();
    }
  };

  const HoverableLink = ({ children, onClick, href = null }) => {
    const [hovered, setHovered] = useState(false);
    const style = {
      color: hovered ? "var(--brand-dark)" : "var(--ink-2)",
      fontWeight: 500,
      cursor: "pointer",
      transition: "all .15s ease",
      textDecoration: "none",
      borderRadius: "8px",
      padding: "2px 6px",
      background: hovered ? "var(--brand-wash)" : "transparent",
    };
    if (href) {
      return (
        <a
          href={href}
          rel="noopener noreferrer"
          style={style}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {children}
        </a>
      );
    }
    return (
      <span
        onClick={onClick}
        style={style}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {children}
      </span>
    );
  };

  const toggleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
const handleExport = async (format) => {
  setExporting(true);
  try {
    const params = new URLSearchParams();
    params.set("format", format);
    if (filterJson) params.set("filter_json", filterJson);
    const res = await axios.get(`${apiBaseUrl}/export/${resolvedTableName}?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${resolvedTableName}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("❌ Export failed:", err);
    alert("❌ Export failed.");
  } finally {
    setExporting(false);
  }
};

const toggleSelectAll = () => {
  const allSelectedOnPage = pageSelectedCount === pageIds.length && pageIds.length > 0;
  if (allSelectedOnPage) {
    // unselect only the visible ones
    setSelectedRows(prev => prev.filter(id => !pageIds.includes(id)));
  } else {
    // add all visible ones (keep any already-selected from other pages)
    setSelectedRows(prev => Array.from(new Set([...prev, ...pageIds])));
  }
};


const displayedFields = useMemo(() => {
  return activeViewColumns
    ? fields
        .filter((f) => activeViewColumns[f.name])
        .sort((a, b) => activeViewColumns[a.name].order - activeViewColumns[b.name].order)
    : fields.filter((f) => f.name !== "id");
}, [fields, activeViewColumns]);
  const doBulkDelete = async () => {
    if (!selectedRows.length) return;
    if (!window.confirm(`Delete ${selectedRows.length} record(s)?`)) return;
    try {
      await Promise.all(
        selectedRows.map((id) =>
          axios.delete(`${apiBaseUrl}/${resolvedTableName}/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      setRecords((prev) => prev.filter((rec) => !selectedRows.includes(rec.id)));
      setSelectedRows([]);
      alert("✅ Records deleted!");
    } catch (err) {
      console.error("❌ Bulk delete failed:", err);
      alert("❌ Failed to delete some records.");
    }
  };

  const isEditingCell = (rowId, fieldName) =>
    editing && editing.id === rowId && editing.field === fieldName;

  // ----- handle reference selection from modal
  const handleReferencePicked = async (picked) => {
    // picked = { id, display_value }
    const { row, field } = refPicker;
    setRefPicker({ open: false, row: null, field: null });
    if (!row || !field) return;

    const fieldName = field.name;
    const cellKey = keyOf(row.id, fieldName);
    const oldVal = row[fieldName];

    // optimistic with display_value
    const newObj = { id: picked.id, display_value: picked.display_value };
    setSavingCells((s) => new Set([...s, cellKey]));
    setRecords((list) =>
      list.map((r) => (r.id === row.id ? { ...r, [fieldName]: newObj } : r))
    );

    try {
      await axios.put(
        `${apiBaseUrl}/${resolvedTableName}/${row.id}`,
        { [fieldName]: picked.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // revert
      setRecords((list) =>
        list.map((r) => (r.id === row.id ? { ...r, [fieldName]: oldVal } : r))
      );
      alert("Failed to set reference.");
    } finally {
      setSavingCells((s) => {
        const n = new Set(s);
        n.delete(cellKey);
        return n;
      });
    }
  };

  return (
  <motion.div
    style={styles.shell}
    initial={{ opacity: 0, x: 28 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -28 }}
    transition={{ duration: 0.28, ease: "easeOut" }}
  >
      {!hideHeader && (
        <div style={styles.toolbar}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h1 style={styles.title}>{tableLabel || resolvedTableName}</h1>
            {availableViews.length > 0 && (
              <div style={styles.viewPicker}>
                <label style={styles.viewLabel}>View</label>
                <select
                  value={selectedViewId || ""}
                  onChange={(e) => setSelectedViewId(e.target.value)}
                  style={styles.select}
                >
                  {availableViews.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.sip_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={() => {
                let url = `/dashboard/${resolvedTableName}/form_view/new`;
                if (referenceFilter?.field && referenceFilter?.value) {
                  const displayValue = referenceFilter.display_value || referenceFilter.value;
                  url += `?refField=${referenceFilter.field}&refValue=${referenceFilter.value}&refDisplay=${encodeURIComponent(displayValue)}`;
                }
                navigate(url);
              }}
              className="sn-btn sn-btn--primary"
            >
              <Plus size={15} /> New
            </button>

            <button
              onClick={() => setUiBuilderOpen(true)}
              className="sn-btn sn-btn--ghost"
              title="Customize columns & layout"
            >
              <Palette size={15} /> UI Builder
            </button>

            <button
              onClick={() => setImportWizardOpen(true)}
              className="sn-btn sn-btn--ghost"
              title="Import data from CSV/Excel"
            >
              <UploadCloud size={15} /> Import
            </button>

            <button
              onClick={() => handleExport("csv")}
              className="sn-btn sn-btn--ghost"
              title="Export visible data as CSV"
              disabled={exporting}
            >
              <Download size={15} /> Export
            </button>

            {resolvedTableName === "ml_models" && (
              <button
                onClick={() => setModelWizardOpen(true)}
                className="sn-btn sn-btn--primary"
                title="Set up and train a new prediction model"
              >
                <Sparkles size={15} /> Build Model
              </button>
            )}
            {/* --- GROUP BY CONTROLS --- */}
<div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
    <label style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Group by</label>
    <select
      value={groupBy1}
      onChange={(e) => { setGroupBy1(e.target.value); setOffset(0); setSearchTrigger(p=>p+1); }}
      style={styles.select}
    >
      <option value="">— None —</option>
      {fields.filter(f => f.name !== "id").map(f => (
        <option key={f.name} value={f.name}>{f.label}</option>
      ))}
    </select>

    {groupBy1 && (
      <>
        <span style={{ color: "var(--muted)" }}>/</span>
        <select
          value={groupBy2}
          onChange={(e) => { setGroupBy2(e.target.value); setOffset(0); setSearchTrigger(p=>p+1); }}
          style={styles.select}
        >
          <option value="">(no second level)</option>
          {fields
            .filter(f => f.name !== "id" && f.name !== groupBy1)
            .map(f => <option key={f.name} value={f.name}>{f.label}</option>)
          }
        </select>
      </>
    )}
  </div>

  {groupBy1 && (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      <label style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Metric</label>
      <select
        value={metricFn}
        onChange={(e) => { setMetricFn(e.target.value); setOffset(0); setSearchTrigger(p=>p+1); }}
        style={styles.select}
      >
        <option value="count">Count</option>
        <option value="sum">Sum</option>
        <option value="avg">Average</option>
        <option value="min">Min</option>
        <option value="max">Max</option>
      </select>
      <select
        value={metricField}
        onChange={(e) => { setMetricField(e.target.value); setOffset(0); setSearchTrigger(p=>p+1); }}
        style={styles.select}
        disabled={metricFn === "count"}
        title={metricFn === "count" ? "Count uses the 'id' field" : "Choose a numeric field"}
      >
        {[{name:"id", label:"id"}, ...fields].map(f => (
          <option key={f.name} value={f.name}>{f.label}</option>
        ))}
      </select>

      <button
        onClick={() => { setGroupBy1(""); setGroupBy2(""); setMetricFn("count"); setMetricField("id"); setOffset(0); setSearchTrigger(p=>p+1); }}
        className="sn-btn sn-btn--ghost"
      >
        <X size={14} /> Clear
      </button>
    </div>
  )}
</div>

          </div>
        </div>
      )}

      <div style={styles.card}>
        <div style={{ ...styles.cardHeader, justifyContent: "flex-start" }}>
          <button
            onClick={() => setShowAdvancedFilter((s) => !s)}
            className={`sn-btn ${showAdvancedFilter ? "sn-btn--neutral" : "sn-btn--primary"}`}
          >
            <SlidersHorizontal size={14} />
            Advanced Filter
            {showAdvancedFilter ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
        {showAdvancedFilter && (
          <div style={{ padding: "10px 12px 2px" }}>
            <FilterBuilderComponent
              filterTable={resolvedTableName}
              filterValue={filterJson}
              onChange={(val) => {
                setFilterJson(val);
                const sp = new URLSearchParams(searchParams.toString());
                if (val) sp.set("filter_json", val);
                else sp.delete("filter_json");
                setSearchParams(sp, { replace: true });
                setOffset(0);
                setSearchTrigger((p) => p + 1);
              }}
              token={token}
              apiBaseUrl={apiBaseUrl}
              isEmbeddedInListView={true}
            />
          </div>
        )}
      </div>

      {selectedRows.length > 0 && (
        <div style={styles.selectionBar}>
          <div>
            <strong>{selectedRows.length}</strong> selected
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {bulkActions.map((action) => (
              <button
                key={action.id}
                onClick={() => runBulkAction(action)}
                className={`sn-btn sn-btn--${action.sip_variant === "primary" ? "primary" : "neutral"}`}
                title={action.sip_tooltip || ""}
              >
                {action.sip_icon ? `${action.sip_icon} ` : ""}
                {action.sip_label || action.sip_name}
              </button>
            ))}
            <button onClick={doBulkDelete} className="sn-btn sn-btn--danger">
              <Trash2 size={14} /> Delete Selected
            </button>
            <button onClick={() => setSelectedRows([])} className="sn-btn sn-btn--neutral">
              <X size={14} /> Clear
            </button>
          </div>
        </div>
      )}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
  <tr style={styles.headRow}>
    <th style={{ ...styles.th, width: 42 }} />
    <th style={{ ...styles.th, width: 54, textAlign: "center" }}>
  {!isGrouped && (
    <input
      ref={headerCbRef}
      type="checkbox"
      title="Select all visible"
      onChange={toggleSelectAll}
      disabled={loading || pageIds.length === 0}
      checked={pageIds.length > 0 && pageSelectedCount === pageIds.length}
    />
  )}
</th>

    {isGrouped ? (
      <>
        <th style={styles.th}>
          <div style={styles.thLabel}>
            {fields.find(f => f.name === groupBy1)?.label || groupBy1}
            {groupBy2 ? ` / ${fields.find(f => f.name === groupBy2)?.label || groupBy2}` : ""}
          </div>
        </th>
        <th
          style={styles.th}
          onClick={() => {
            const metricKey = metricFn === "count"
              ? `count_${metricField || "id"}`
              : `${metricFn}_${metricField}`;
            if (sortBy === metricKey) setSortOrder(prev => prev === "asc" ? "desc" : "asc");
            else { setSortBy(metricKey); setSortOrder("asc"); }
            setSearchTrigger(p => p + 1);
          }}
          title="Sort by metric"
        >
          Metric ({metricFn}{metricFn !== "count" ? `:${metricField}` : ""})
          {sortBy?.startsWith(metricFn) || sortBy?.startsWith("count_")
            ? (sortOrder === "asc" ? " ▲" : " ▼")
            : ""}
        </th>
      </>
    ) : (
      displayedFields.map((field) => (
        <th key={field.name} style={styles.th}>
          <div
            style={styles.thLabel}
            onClick={() => {
              if (sortBy === field.name) {
                setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
              } else {
                setSortBy(field.name);
                setSortOrder("asc");
              }
            }}
            title="Sort"
          >
            {field.label}
            {sortBy === field.name ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
          </div>
        </th>
      ))
    )}
  </tr>

  {/* Hide column filter inputs when grouped */}
  {!isGrouped && (
    <tr style={styles.filterRow}>
      <th style={styles.filterTh} />
      <th style={styles.filterTh} />
      {displayedFields.map((field) => (
        <th key={field.name} style={styles.filterTh}>
          <input
            type="text"
            placeholder={
              field.field_type.includes("DATE")
                ? "YYYY/MM/DD or * contains"
                : "Filter…"
            }
            defaultValue={columnFilters[field.name] || ""}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setColumnFilters((prev) => ({ ...prev, [field.name]: e.target.value }));
                setOffset(0);
                setSearchTrigger((p) => p + 1);
              }
            }}
            style={styles.input}
          />
        </th>
      ))}
    </tr>
  )}
</thead>


         <tbody>
    {loading && !initialLoaded ? (
    // 🔹 only show skeletons when we have never loaded data yet
    Array.from({ length: 8 }).map((_, i) => (
      <tr key={`sk-${i}`} style={i % 2 ? styles.zebra : undefined}>
        <td style={styles.td}><div style={styles.skel} /></td>
        <td style={styles.td}><div style={styles.skel} /></td>
        {isGrouped
          ? [0, 1].map((n) => (
              <td key={`gsk-${n}`} style={styles.td}>
                <div style={styles.skel} />
              </td>
            ))
          : displayedFields.map((f) => (
              <td key={f.name} style={styles.td}>
                <div style={styles.skel} />
              </td>
            ))}
      </tr>
    ))
  ) : isGrouped ? (
    records.length ? (
      records.map((row, i) => {
        const mKey = metricFn === "count" ? `count_${metricField || "id"}` : `${metricFn}_${metricField}`;
        const g1 = row[groupBy1 + "__display"] ?? row[groupBy1] ?? "—";
        const g2 = groupBy2 ? (row[groupBy2 + "__display"] ?? row[groupBy2] ?? "—") : null;

        return (
          <tr key={`g-${i}`} style={i % 2 ? styles.zebra : undefined}>
            <td style={styles.td}></td>
            <td style={styles.td}></td>
           <td style={styles.td}>
  <span style={styles.badge}>{String(g1)}</span>
  {groupBy2 && (
    <span style={{ ...styles.badge, marginLeft: 8 }}>
      {String(g2)}
    </span>
  )}
</td>

            <td style={{ ...styles.td, fontWeight: 700 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>{row[mKey] ?? 0}</span>
                <button
                  onClick={() => {
                    const nextFilters = { ...columnFilters };
                    nextFilters[groupBy1] = String(row[groupBy1] ?? "");
                    if (groupBy2) nextFilters[groupBy2] = String(row[groupBy2] ?? "");
                    setColumnFilters(nextFilters);
                    setGroupBy1("");
                    setGroupBy2("");
                    setOffset(0);
                    setSearchTrigger(p => p + 1);
                  }}
                  className="sn-btn sn-btn--ghost"
                  title="Drill into this group"
                >
                  <ArrowRightCircle size={14} /> Drill in
                </button>
              </div>
            </td>
          </tr>
        );
      })
    ) : (
      <tr>
        <td colSpan={4} style={styles.empty}>
          <Sparkles size={22} color="var(--muted)" style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 16, color: "var(--ink-2)", fontWeight: 700 }}>
            No groups found
          </div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 6 }}>
            Try adjusting filters or grouping fields.
          </div>
        </td>
      </tr>
    )
  ) : (
    /* your existing non-grouped rendering (unchanged) */
    records.length > 0 ? (
      records.map((record, i) => (
        // ... keep your original row rendering here ...
        <motion.tr
  key={record.id}
  style={i % 2 ? styles.zebra : undefined}
  className="lv-row"
  initial={{ opacity: 0, y: 10 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.18, delay: Math.min(i * 0.015, 0.18) }}
>
          <td style={{ ...styles.td, textAlign: "center" }}>
            <a href={`/dashboard/${resolvedTableName}/form_view/${record.id}`} style={styles.iconBtn} title="Open record">
              <ExternalLink size={15} />
            </a>
          </td>
          <td style={styles.td}>
            <input type="checkbox" checked={selectedRows.includes(record.id)} onChange={() => toggleRowSelection(record.id)} />
          </td>
          {displayedFields.map((field) => {
            const cellKey = keyOf(record.id, field.name);
            const saving = savingCells.has(cellKey);
            const editable = isEditableField(field);
            const isRef = !!field.reference_table;
            const isEditing = isEditingCell(record.id, field.name);
            const value = record[field.name];

            return (
              <td
                key={field.name}
                style={{ ...styles.td, position: "relative", cursor: editable ? "cell" : "default" }}
                onDoubleClick={() => startEdit(record, field)}
                title={isRef ? "Double-click to pick reference" : "Double-click to edit"}
              >
                {isEditing && !isRef ? (
                  renderEditor(record, field)
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                      {typeof value === "boolean" ? renderEditor(record, field) : formatValue(value, field, record)}
                    </div>
                    {saving && <span style={styles.spinner} title="Saving…" />}
                    {editable && !saving && (
                      <span style={styles.pencilHint}>
                        <Pencil size={11} />
                      </span>
                    )}
                  </div>
                )}
              </td>
            );
          })}
        </motion.tr>
      ))
    ) : (
      <tr>
        <td colSpan={displayedFields.length + 2} style={styles.empty}>
          <Sparkles size={22} color="var(--muted)" style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 16, color: "var(--ink-2)", fontWeight: 700 }}>
            No records found
          </div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 6 }}>
            Try adjusting filters or create a new record.
          </div>
        </td>
      </tr>
    )
  )}
</tbody>

        </table>
      </div>

      <div style={styles.footer}>
        <div style={{ minWidth: 200 }}>
          <label style={{ marginRight: 8, color: "var(--ink-2)" }}>Rows per page</label>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setOffset(0);
            }}
            style={styles.select}
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        <div style={styles.pager}>
          <button
            onClick={() => setOffset(0)}
            disabled={offset === 0}
            className="sn-btn sn-btn--ghost"
            title="First"
          >
            <ChevronsLeft size={14} />
          </button>
          <button
            onClick={() => setOffset((p) => Math.max(0, p - limit))}
            disabled={offset === 0}
            className="sn-btn sn-btn--ghost"
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <span style={{ color: "var(--ink-2)", fontSize: 13 }}>
            Page <strong>{Math.floor(offset / limit) + 1}</strong> of{" "}
            <strong>{Math.max(1, Math.ceil((totalRecords || 0) / limit))}</strong>
          </span>
          <button
            onClick={() => {
              const next = offset + limit;
              if (next < totalRecords) setOffset(next);
            }}
            disabled={offset + limit >= totalRecords}
            className="sn-btn sn-btn--ghost"
          >
            Next <ChevronRight size={14} />
          </button>
          <button
            onClick={() => {
              const lastPage = Math.max(0, Math.ceil((totalRecords || 0) / limit) - 1);
              setOffset(lastPage * limit);
            }}
            disabled={offset + limit >= totalRecords}
            className="sn-btn sn-btn--ghost"
            title="Last"
          >
            <ChevronsRight size={14} />
          </button>
        </div>

        <div style={{ color: "var(--muted)" }}>
          Total: <strong>{totalRecords}</strong>
        </div>
      </div>

      {/* UI Builder */}
      <UiBuilderModal
        isOpen={isUiBuilderOpen}
        onClose={() => setUiBuilderOpen(false)}
        onSaved={() => {
          setUiBuilderOpen(false);
          window.location.reload();
        }}
        tableId={tableId}
        tableName={resolvedTableName}
        defaultViewType="list"
      />

      {/* Import wizard */}
      <ImportWizard
        isOpen={importWizardOpen}
        onClose={() => setImportWizardOpen(false)}
        tableName={resolvedTableName}
        tableLabel={tableLabel}
        fields={fields}
        apiBaseUrl={apiBaseUrl}
        token={token}
        onComplete={() => setSearchTrigger((p) => p + 1)}
      />

      {/* Model training wizard */}
      <ModelTrainingWizard
        isOpen={modelWizardOpen}
        onClose={() => setModelWizardOpen(false)}
        apiBaseUrl={apiBaseUrl}
        token={token}
        onComplete={() => setSearchTrigger((p) => p + 1)}
      />

      {/* Reference picker */}
      <ReferenceModal
        isOpen={refPicker.open}
        title={`Select ${refPicker.field?.label || refPicker.field?.name || "record"}`}
        referenceTable={refPicker.field?.reference_table}
        onSelect={handleReferencePicked}
        onClose={() => setRefPicker({ open: false, row: null, field: null })}
        token={token}
        apiBaseUrl={apiBaseUrl}
        viewType="list"
      />
   </motion.div>
  );

  // editor renderer (text/number/date/datetime/boolean)
 function renderEditor(record, field) {
  const t = (field.field_type || "").toUpperCase();

  // ✅ BOOLEAN: inline checkbox with optimistic save
  if (t.includes("BOOL")) {
    const cellKey = keyOf(record.id, field.name);
    const checked = !!record[field.name];
    const saving = savingCells.has(cellKey);

    const toggle = async (next) => {
      setSavingCells((s) => new Set([...s, cellKey]));
      // optimistic update
      setRecords((list) =>
        list.map((r) => (r.id === record.id ? { ...r, [field.name]: next } : r))
      );

      try {
        await axios.put(
          `${apiBaseUrl}/${resolvedTableName}/${record.id}`,
          { [field.name]: next },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        // revert on error
        setRecords((list) =>
          list.map((r) => (r.id === record.id ? { ...r, [field.name]: checked } : r))
        );
        alert("Failed to save change.");
      } finally {
        setSavingCells((s) => {
          const n = new Set(s);
          n.delete(cellKey);
          return n;
        });
      }
    };

    return (
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <input
          type="checkbox"
          checked={checked}
          disabled={saving}
          onChange={(e) => toggle(e.target.checked)}
        />
        {saving && <span style={styles.spinner} title="Saving…" />}
      </label>
    );
  }

  // ENUM: inline select with choices (your existing code) …
  if (t === "ENUM") {
    const cellKey = keyOf(record.id, field.name);
    const saving = savingCells.has(cellKey);
    const options = field.allowed_options || [];
    return (
      <select
        autoFocus
        value={String(draft ?? "")}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commitEdit();
          if (e.key === "Escape") cancelEdit();
        }}
        style={styles.editorInput}
      >
        <option value="">— Select —</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label || opt.value}
          </option>
        ))}
      </select>
    );
  }

  // generic editors (unchanged) …
  const commonProps = {
    autoFocus: true,
    value: draft,
    onChange: (e) => setDraft(e.target.value),
    onBlur: commitEdit,
    onKeyDown: (e) => {
      if (e.key === "Enter") commitEdit();
      if (e.key === "Escape") cancelEdit();
    },
    style: styles.editorInput,
  };

  if (t.includes("TIMESTAMP")) return <input type="datetime-local" {...commonProps} />;
  if (t.includes("DATE")) return <input type="date" {...commonProps} />;
  if (t.includes("INT") || t.includes("NUMERIC") || t.includes("DECIMAL"))
    return <input type="number" step="any" {...commonProps} />;

  return <input type="text" {...commonProps} />;
}


};

/* ---------- styles ---------- */
const styles = {
  shell: {
    padding: 24,
    background: "var(--bg)",
  },

  toolbar: {
    marginBottom: 16,
    padding: "14px 18px",
    borderRadius: 12,
    background: "#ffffff",
    border: "1px solid var(--line)",
    boxShadow: "var(--shadow-sm)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },

  title: {
    fontSize: 21,
    fontWeight: 800,
    letterSpacing: "-0.01em",
    color: "var(--ink)",
    margin: 0,
  },

  viewPicker: { display: "flex", alignItems: "center", gap: 8 },
  viewLabel: { fontSize: 12, color: "var(--muted)", fontWeight: 600 },

  card: {
    background: "#fff",
    border: "1px solid var(--line)",
    borderRadius: 12,
    marginBottom: 14,
    overflow: "hidden",
    boxShadow: "var(--shadow-xs)",
  },
  cardHeader: {
    padding: "10px 14px",
    borderBottom: "1px solid var(--line)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "var(--card-2)",
  },

  selectionBar: {
    background: "var(--brand-wash)",
    border: "1px solid transparent",
    color: "var(--brand-dark)",
    borderRadius: 12,
    padding: "10px 14px",
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontWeight: 600,
  },

  tableWrap: {
    border: "1px solid var(--line)",
    borderRadius: 12,
    overflow: "auto",
    background: "#fff",
    boxShadow: "var(--shadow-xs)",
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 13,
    tableLayout: "fixed",   // make widths stable
  },


  headRow: {
    position: "sticky",
    top: 0,
    background: "var(--card-2)",
    zIndex: 2,
  },
  th: {
    position: "sticky",
    top: 0,
    background: "var(--card-2)",
    textAlign: "left",
    fontWeight: 700,
    color: "var(--ink-2)",
    borderBottom: "1px solid var(--line)",
    padding: "10px 12px",
    whiteSpace: "nowrap",
  },

  thLabel: {
    display: "inline-block",
    cursor: "pointer",
    userSelect: "none",
  },

  filterRow: {
    position: "sticky",
    top: 42, // sits below the header labels
    background: "#fff",
    zIndex: 1,
  },
  filterTh: {
    borderBottom: "1px solid var(--line)",
    padding: "8px 12px",
    background: "#fff",
  },

  input: {
    width: "100%",
    padding: "7px 8px",
    borderRadius: 8,
    border: "1px solid var(--line)",
    outline: "none",
    fontSize: 12.5,
  },

  td: {
    padding: "10px 12px",
    borderBottom: "1px solid var(--line)",
    color: "var(--ink)",
    whiteSpace: "nowrap",
    maxWidth: 320,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  editorInput: {
    width: "100%",
    padding: "6px 8px",
    borderRadius: 8,
    border: "1px solid var(--brand)",
    outline: "none",
    fontSize: 12.5,
    background: "#fff",
    boxShadow: "0 0 0 3px var(--brand-wash)",
  },

  zebra: {
    background: "var(--card-2)",
  },

  empty: {
    textAlign: "center",
    padding: "40px 12px",
    background: "#fff",
  },

  footer: {
    marginTop: 12,
    padding: 12,
    border: "1px solid var(--line)",
    background: "#fff",
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    boxShadow: "var(--shadow-xs)",
  },

  pager: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  // Buttons
  btn: {
    border: "1px solid var(--line)",
    background: "#fff",
    color: "var(--ink-2)",
    padding: "8px 12px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
    transition: "all .15s ease",
  },
  btnPrimary: {
    background: "var(--brand)",
    borderColor: "var(--brand)",
    color: "#fff",
  },
  btnGhost: {
    background: "#fff",
  },
  btnInfo: {
    background: "var(--brand-2)",
    borderColor: "var(--brand-2)",
    color: "#fff",
  },
  btnMuted: {
    background: "var(--card-2)",
    color: "var(--ink-2)",
  },
  btnDanger: {
    background: "var(--danger)",
    borderColor: "var(--danger)",
    color: "#fff",
  },

  select: {
    padding: "7px 8px",
    borderRadius: 8,
    border: "1px solid var(--line)",
    outline: "none",
    fontSize: 13,
    background: "#fff",
  },

  iconBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 5,
    borderRadius: 8,
    textDecoration: "none",
    color: "var(--brand)",
  },

  badge: {
    display: "inline-block",
    padding: "2px 9px",
    borderRadius: 999,
    background: "var(--brand-wash)",
    color: "var(--brand-dark)",
    fontSize: 12,
    fontWeight: 600,
  },

  skel: {
    height: 10,
    width: "100%",
    background: "linear-gradient(90deg,var(--card-2),#ffffff,var(--card-2))",
    borderRadius: 6,
    animation: "lv-skel 1.2s infinite",
  },

  pencilHint: {
    opacity: 0.35,
    fontSize: 12,
    color: "var(--brand)",
  },

  spinner: {
    width: 12,
    height: 12,
    border: "2px solid var(--line)",
    borderTopColor: "var(--brand)",
    borderRadius: "50%",
    animation: "lv-spin .8s linear infinite",
    display: "inline-block",
  },
};



export default ListView;
